import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import {
  Camera, Upload, Apple, Flame, Zap, TrendingUp, Download, Loader2,
  Plus, Eye, Utensils, Beef, Wheat, Droplet
} from 'lucide-react';
import { toast } from 'sonner';

export default function Nutrition() {
  const [history, setHistory] = useState({ meals: [], daily_totals: {} });
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [showScan, setShowScan] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [mealType, setMealType] = useState('lunch');
  const fileRef = useRef(null);

  const fetchHistory = useCallback(async () => {
    try { const r = await api.get('/nutrition/history?days=30'); setHistory(r.data); }
    catch (err) { console.error(err); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const handlePhotoAnalysis = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image'); return; }
    if (file.size > 5000000) { toast.error('Image too large (max 5MB)'); return; }

    setAnalyzing(true);
    setShowScan(true);
    setScanResult(null);

    const reader = new FileReader();
    reader.onload = async (ev) => {
      const base64 = ev.target.result;
      try {
        const res = await api.post('/nutrition/analyze-meal', { image: base64, meal_type: mealType });
        setScanResult(res.data.analysis);
        toast.success('Meal analyzed!');
        fetchHistory();
      } catch (err) { toast.error('Analysis failed'); }
      finally { setAnalyzing(false); }
    };
    reader.readAsDataURL(file);
  };

  // Compute stats
  const meals = history.meals || [];
  const daily = history.daily_totals || {};
  const today = new Date().toISOString().slice(0, 10);
  const todayTotals = daily[today] || { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0, meals: 0 };
  const chartData = Object.entries(daily).sort().slice(-7).map(([date, d]) => ({
    date: date.slice(5),
    Protein: Math.round(d.protein_g),
    Carbs: Math.round(d.carbs_g),
    Fat: Math.round(d.fat_g),
    Calories: Math.round(d.calories),
  }));

  // Weekly meal plan
  const weekPlan = [
    { day: 'Mon', type: 'Match Day', cal: 2800, p: 160, c: 420, f: 70, color: 'bg-red-50 border-red-200' },
    { day: 'Tue', type: 'Strength', cal: 2600, p: 140, c: 380, f: 65, color: 'bg-blue-50 border-blue-200' },
    { day: 'Wed', type: 'Skills', cal: 2650, p: 135, c: 400, f: 68, color: 'bg-emerald-50 border-emerald-200' },
    { day: 'Thu', type: 'Match Day', cal: 2850, p: 155, c: 430, f: 72, color: 'bg-red-50 border-red-200' },
    { day: 'Fri', type: 'Recovery', cal: 2400, p: 130, c: 320, f: 60, color: 'bg-purple-50 border-purple-200' },
    { day: 'Sat', type: 'Power', cal: 2620, p: 145, c: 390, f: 67, color: 'bg-blue-50 border-blue-200' },
    { day: 'Sun', type: 'Rest', cal: 2350, p: 120, c: 300, f: 62, color: 'bg-slate-50 border-slate-200' },
  ];

  if (loading) return <div className="space-y-4"><Skeleton className="h-8 w-48" /><div className="grid grid-cols-4 gap-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}</div><Skeleton className="h-64 rounded-xl" /></div>;

  return (
    <div className="space-y-5" data-testid="nutrition-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-display font-semibold">Nutrition AI</h1>
          <p className="text-sm text-muted-foreground">AI-powered meal analysis & performance optimization</p>
        </div>
        <div className="flex gap-2">
          <Select value={mealType} onValueChange={setMealType}><SelectTrigger className="w-28 h-9"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="breakfast">Breakfast</SelectItem><SelectItem value="lunch">Lunch</SelectItem><SelectItem value="dinner">Dinner</SelectItem><SelectItem value="snack">Snack</SelectItem><SelectItem value="pre-match">Pre-Match</SelectItem><SelectItem value="post-match">Post-Match</SelectItem></SelectContent></Select>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => fileRef.current?.click()}>
            <Camera className="h-4 w-4 mr-2" /> Scan Meal
          </Button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoAnalysis} />
        </div>
      </div>

      {/* Today's Macros */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Card className="bg-amber-50/60 border-amber-200/50"><CardContent className="p-3 text-center">
          <Flame className="h-4 w-4 mx-auto text-amber-500 mb-1" />
          <p className="text-xl font-display font-bold tabular-nums text-amber-600">{todayTotals.calories}</p>
          <p className="text-[10px] text-muted-foreground">Calories</p>
        </CardContent></Card>
        <Card className="bg-red-50/60 border-red-200/50"><CardContent className="p-3 text-center">
          <Beef className="h-4 w-4 mx-auto text-red-500 mb-1" />
          <p className="text-xl font-display font-bold tabular-nums text-red-600">{todayTotals.protein_g}g</p>
          <p className="text-[10px] text-muted-foreground">Protein</p>
        </CardContent></Card>
        <Card className="bg-amber-50/60 border-amber-200/50"><CardContent className="p-3 text-center">
          <Wheat className="h-4 w-4 mx-auto text-amber-600 mb-1" />
          <p className="text-xl font-display font-bold tabular-nums text-amber-700">{todayTotals.carbs_g}g</p>
          <p className="text-[10px] text-muted-foreground">Carbs</p>
        </CardContent></Card>
        <Card className="bg-blue-50/60 border-blue-200/50"><CardContent className="p-3 text-center">
          <Droplet className="h-4 w-4 mx-auto text-blue-500 mb-1" />
          <p className="text-xl font-display font-bold tabular-nums text-blue-600">{todayTotals.fat_g}g</p>
          <p className="text-[10px] text-muted-foreground">Fat</p>
        </CardContent></Card>
        <Card className="bg-emerald-50/60 border-emerald-200/50"><CardContent className="p-3 text-center">
          <Utensils className="h-4 w-4 mx-auto text-emerald-500 mb-1" />
          <p className="text-xl font-display font-bold tabular-nums text-emerald-600">{todayTotals.meals}</p>
          <p className="text-[10px] text-muted-foreground">Meals Today</p>
        </CardContent></Card>
      </div>

      <Tabs defaultValue="history">
        <TabsList className="flex flex-wrap h-auto gap-1 bg-transparent p-0">
          <TabsTrigger value="history" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white bg-emerald-100 text-emerald-700 text-xs rounded-lg px-4"><Apple className="h-3.5 w-3.5 mr-1" /> Meal Log</TabsTrigger>
          <TabsTrigger value="charts" className="data-[state=active]:bg-blue-500 data-[state=active]:text-white bg-blue-100 text-blue-700 text-xs rounded-lg px-4"><TrendingUp className="h-3.5 w-3.5 mr-1" /> Charts</TabsTrigger>
          <TabsTrigger value="plan" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white bg-purple-100 text-purple-700 text-xs rounded-lg px-4"><Zap className="h-3.5 w-3.5 mr-1" /> Weekly Plan</TabsTrigger>
        </TabsList>

        {/* Meal Log */}
        <TabsContent value="history">
          <div className="space-y-3">
            {meals.length === 0 ? (
              <Card className="border-dashed"><CardContent className="flex flex-col items-center py-12">
                <Camera className="h-12 w-12 text-muted-foreground/40 mb-4" />
                <h3 className="text-lg font-semibold mb-1">No meals logged</h3>
                <p className="text-sm text-muted-foreground mb-4">Scan a meal photo to get AI nutrition analysis</p>
                <Button onClick={() => fileRef.current?.click()} className="bg-emerald-600 hover:bg-emerald-700"><Camera className="h-4 w-4 mr-2" /> Scan Your First Meal</Button>
              </CardContent></Card>
            ) : meals.map(meal => {
              const a = meal.analysis || {};
              const items = a.items || [];
              const t = a.total || {};
              return (
                <Card key={meal.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <Badge className="text-xs capitalize bg-emerald-100 text-emerald-700 border-0">{meal.meal_type}</Badge>
                          <span className="text-xs text-muted-foreground">{meal.date} {meal.time}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-center"><p className="text-sm font-bold tabular-nums text-amber-600">{t.calories}</p><p className="text-[9px] text-muted-foreground">cal</p></div>
                        <div className="text-center"><p className="text-sm font-bold tabular-nums text-red-600">{t.protein_g}g</p><p className="text-[9px] text-muted-foreground">P</p></div>
                        <div className="text-center"><p className="text-sm font-bold tabular-nums text-amber-700">{t.carbs_g}g</p><p className="text-[9px] text-muted-foreground">C</p></div>
                        <div className="text-center"><p className="text-sm font-bold tabular-nums text-blue-600">{t.fat_g}g</p><p className="text-[9px] text-muted-foreground">F</p></div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {items.map((item, i) => (
                        <Badge key={i} variant="outline" className="text-[10px]">{item.name} {item.portion_g}g</Badge>
                      ))}
                    </div>
                    {a.sport_impact && <p className="text-xs text-emerald-600 mt-2 italic">{a.sport_impact}</p>}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Charts */}
        <TabsContent value="charts">
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm">7-Day Macro Breakdown</CardTitle></CardHeader>
            <CardContent>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 22%, 88%)" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Protein" fill="#ef4444" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Carbs" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Fat" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-sm text-muted-foreground text-center py-8">Log meals to see your nutrition trends</p>}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Weekly Plan */}
        <TabsContent value="plan">
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Zap className="h-4 w-4 text-purple-600" /> AI-Optimized Weekly Plan</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {weekPlan.map(d => (
                  <div key={d.day} className={`flex items-center gap-3 p-3 rounded-xl border ${d.color}`}>
                    <div className="w-10 text-center"><p className="text-sm font-bold">{d.day}</p><p className="text-[9px] text-muted-foreground">{d.type}</p></div>
                    <div className="flex-1 grid grid-cols-4 gap-2">
                      <div className="text-center"><p className="text-xs font-bold tabular-nums">{d.p}g</p><p className="text-[8px] text-muted-foreground">Protein</p></div>
                      <div className="text-center"><p className="text-xs font-bold tabular-nums">{d.c}g</p><p className="text-[8px] text-muted-foreground">Carbs</p></div>
                      <div className="text-center"><p className="text-xs font-bold tabular-nums">{d.f}g</p><p className="text-[8px] text-muted-foreground">Fat</p></div>
                      <div className="text-center"><p className="text-xs font-bold tabular-nums">{d.cal}</p><p className="text-[8px] text-muted-foreground">Cal</p></div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Scan Result Dialog */}
      <Dialog open={showScan} onOpenChange={setShowScan}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Camera className="h-5 w-5 text-emerald-600" /> Meal Analysis</DialogTitle></DialogHeader>
          {analyzing ? (
            <div className="flex flex-col items-center py-8"><Loader2 className="h-10 w-10 text-emerald-500 animate-spin mb-3" /><p className="text-sm font-medium">AI analyzing your meal...</p><p className="text-xs text-muted-foreground">Identifying foods & calculating macros</p></div>
          ) : scanResult ? (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-2">
                <div className="text-center p-2 rounded-lg bg-amber-50 border border-amber-200"><p className="text-lg font-bold text-amber-600 tabular-nums">{scanResult.total?.calories}</p><p className="text-[9px]">Cal</p></div>
                <div className="text-center p-2 rounded-lg bg-red-50 border border-red-200"><p className="text-lg font-bold text-red-600 tabular-nums">{scanResult.total?.protein_g}g</p><p className="text-[9px]">Protein</p></div>
                <div className="text-center p-2 rounded-lg bg-amber-50 border border-amber-200"><p className="text-lg font-bold text-amber-700 tabular-nums">{scanResult.total?.carbs_g}g</p><p className="text-[9px]">Carbs</p></div>
                <div className="text-center p-2 rounded-lg bg-blue-50 border border-blue-200"><p className="text-lg font-bold text-blue-600 tabular-nums">{scanResult.total?.fat_g}g</p><p className="text-[9px]">Fat</p></div>
              </div>
              <div className="space-y-1.5">
                {(scanResult.items || []).map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-muted/30">
                    <span className="text-sm font-medium">{item.name}</span>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span>{item.portion_g}g</span>
                      <span className="font-semibold text-foreground">{item.calories} cal</span>
                    </div>
                  </div>
                ))}
              </div>
              {scanResult.sport_impact && <p className="text-xs text-emerald-600 bg-emerald-50 p-2 rounded-lg italic">{scanResult.sport_impact}</p>}
              {scanResult.recommendations && <div className="space-y-1">{scanResult.recommendations.map((r, i) => <p key={i} className="text-xs text-muted-foreground">• {r}</p>)}</div>}
              <Button className="w-full" onClick={() => setShowScan(false)}>Done</Button>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
